import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { hasManagingAdminRole, hasSuperAdminRole } from "@/lib/auth/admin-roles";
import { formatDate } from "@/lib/formatting/date-time";
import type {
  StoreAdminStatsResponse,
  StoreKind,
  StoreSubscriptionAssignmentView,
  StoreSubscriptionPaymentView,
  SubscriptionPlanView,
} from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { getBuyerAbsoluteUrl, getRestaurantAbsoluteUrl, getSellerAbsoluteUrl } from "@/lib/routing/portal-urls";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { buttonClass, Drawer, ErrorState, LoadingState } from "@/shared/rd";
import { CopyLinkButton } from "@/shared/stores/components/rd-local";
import { useDrawerPanels } from "@/shared/stores/components/drawer-panel-host";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { useStoreSubscriptionPaymentsQuery } from "@/shared/stores/services/queries/useStoreSubscriptionPaymentsQuery";
import { useStoreByIdQuery } from "@/modules/stores/services/queries/useStoreByIdQuery";
import { useStoreOwnerQuery } from "@/modules/stores/services/queries/useStoreOwnerQuery";
import { useStoreStatsQuery } from "@/modules/stores/services/queries/useStoreStatsQuery";
import { useStoreSubscriptionAssignmentsQuery } from "@/modules/stores/services/queries/useStoreSubscriptionAssignmentsQuery";
import { ImpersonateAction } from "@/modules/stores/components/ImpersonateAction";
import { StoreAccessBanner } from "@/modules/stores/components/StoreAccessBanner";
import { StoreStatsCard } from "@/modules/stores/components/StoreStatsCard";
import { StorefrontTagManagerCard } from "@/modules/stores/components/StorefrontTagManagerCard";
import { StoreControlsCard } from "@/modules/stores/components/StoreControlsCard";
import { StorePaidLimitsCard } from "@/modules/stores/components/StorePaidLimitsCard";
import { StoreSubscriptionsTab } from "@/modules/stores/components/subscriptions/StoreSubscriptionsTab";

const DETAIL_WIDTH = 880;

const defaultStats: StoreAdminStatsResponse = {
  orders: { total: 0, pending: 0, delivered: 0 },
  catalog: { products: 0, categories: 0 },
};

type Props = {
  locale: Locale;
  storeId: string;
  /** The list under the drawer, for the crumb and the badge before the store has loaded. */
  listKind: StoreKind;
  onClose: () => void;
  /** The store no longer exists (a 404): back to the list. */
  onMissing: () => void;
  /** Deleted from the danger zone (its toast is already showing): back to the list. */
  onDeleted: () => void;
};

/**
 * A store's (or a restaurant's) whole record, in the design's big drawer over
 * the list: the links, View seller and Impersonate in the bar; the figures,
 * plan and wallet, Tag Manager, payments, subscription history and the store
 * controls in the body. A form opened from inside it takes over this drawer's
 * body (see `drawer-panel`) — never a second drawer on top.
 *
 * The shell's title bar keeps naming the list under the drawer; the drawer's
 * own head names the store, its link, owner and phone.
 */
// eslint-disable-next-line no-restricted-syntax -- not chrome: the store record's content, composed in the shared RD Drawer.
export function StoreDetailsDrawer({ locale, storeId, listKind, onClose, onMissing, onDeleted }: Props) {
  const t = useTranslations();
  const panels = useDrawerPanels();
  const { data: session } = useSessionQuery();
  const { data, isLoading, error, refetch } = useStoreByIdQuery({ storeId });
  const stats = useStoreStatsQuery({ storeId });
  const assignments = useStoreSubscriptionAssignmentsQuery({ storeId });
  const payments = useStoreSubscriptionPaymentsQuery({ storeId });
  const plans = useSubscriptionsQuery();
  const { data: owner } = useStoreOwnerQuery(data?.seller.id ?? "");
  const storeErrorStatus = getQueryErrorStatus(error);

  useEffect(() => {
    if (storeErrorStatus === 404) onMissing();
  }, [storeErrorStatus, onMissing]);

  const store = data?.store;

  const kind = store?.storeKind ?? listKind;
  const isRestaurant = kind === "RESTAURANT";
  const listTitle = t(isRestaurant ? keys.admin.restaurantsTitle : keys.admin.onlineStoresTitle);
  const isManagingAdmin = hasManagingAdminRole(session?.admin?.roles);
  const storefrontPath = store ? getBuyerAbsoluteUrl(locale, store.storeSlug) : "";
  const statePage = t("admin.storeStatePage");

  const subLoading = stats.isLoading || assignments.isLoading || payments.isLoading || plans.isLoading;

  let body;
  if (isLoading || (store && subLoading)) {
    body = <LoadingState title={t(keys.shell.loadingTitle, { page: statePage })} sub={t(keys.shell.loadingSub)} rows={0} />;
  } else if (storeErrorStatus === 404) {
    body = null;
  } else if (error || !data || !store) {
    body = (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: statePage })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  } else {
    const assignmentRows: StoreSubscriptionAssignmentView[] = assignments.error ? [] : (assignments.data ?? []);
    const paymentRows: StoreSubscriptionPaymentView[] = payments.error ? [] : (payments.data ?? []);
    const planRows: SubscriptionPlanView[] = plans.error ? [] : (plans.data?.items ?? []);
    body = (
      <>
        <StoreAccessBanner storeId={store.id} storeKind={store.storeKind} assignments={assignmentRows} locale={locale} />
        <StoreStatsCard stats={stats.error ? defaultStats : (stats.data ?? defaultStats)} t={t} locale={locale} />
        <StoreSubscriptionsTab
          locale={locale}
          storeId={store.id}
          storeKind={store.storeKind}
          assignments={assignmentRows}
          payments={paymentRows}
          plans={planRows}
          canReversePayments={isManagingAdmin}
          t={t}
          afterPlan={
            // Only for the roles whose session can call the endpoint: a card
            // that 403s on press teaches an admin to distrust the page.
            isManagingAdmin ? <StorefrontTagManagerCard storeId={store.id} storefrontUrl={storefrontPath} /> : null
          }
        />
        {/* A store's own Google Routes / WhatsApp / Gemini limits: super admins
            only, as the endpoint and the Paid services page are. */}
        {hasSuperAdminRole(session?.admin?.roles) ? <StorePaidLimitsCard storeId={store.id} locale={locale} /> : null}
        {/* Status for every admin; edit / deactivate for managing admins;
            delete forever for super admins — the API's own split. */}
        <StoreControlsCard
          store={store}
          locale={locale}
          canManage={isManagingAdmin}
          canDelete={hasSuperAdminRole(session?.admin?.roles)}
          onDeleted={onDeleted}
        />
      </>
    );
  }

  const barActions = store ? (
    <>
      <CopyLinkButton
        path={isRestaurant ? getRestaurantAbsoluteUrl(locale) : getSellerAbsoluteUrl(locale, store.storeSlug)}
        label={t(isRestaurant ? keys.admin.restaurantPortalLabel : keys.admin.storeListSellerDashboardLabel)}
        copiedMessage={t(isRestaurant ? "admin.restaurantPortalLinkCopied" : "admin.sellerDashboardLinkCopied")}
        ariaLabel={t(isRestaurant ? keys.admin.restaurantPortalLabel : keys.admin.shareSellerDashboardTooltip)}
      />
      <CopyLinkButton
        path={storefrontPath}
        icon={ExternalLink}
        label={t(keys.admin.storeListCustomerProductsLabel)}
        copiedMessage={t("admin.storefrontLinkCopied")}
        ariaLabel={t(keys.admin.shareStorefrontTooltip)}
      />
      <Link to={`/${locale}/admin/sellers/${data?.seller.id}`} className={buttonClass("secondary", "sm")} style={{ textDecoration: "none" }}>
        <span>{t(keys.admin.viewSeller)}</span>
      </Link>
      {store.deactivatedAt ? null : (
        <ImpersonateAction
          storeId={store.id}
          locale={locale}
          adminRoles={session?.admin?.roles}
          portal={isRestaurant ? "restaurant" : "seller"}
        />
      )}
    </>
  ) : null;

  const subParts = store
    ? [
        <bdi key="slug" dir="ltr">
          {store.storeSlug}
        </bdi>,
        owner?.seller?.name ? (
          <span key="owner" dir="auto">
            {owner.seller.name}
          </span>
        ) : null,
        owner?.seller?.phone ? (
          <bdi key="phone" dir="ltr">
            {owner.seller.phone}
          </bdi>
        ) : null,
        <span key="created">{t("admin.storeDrawerCreated", { date: formatDate(store.createdAt, locale) })}</span>,
      ].filter(Boolean)
    : [];

  return panels.wrap(
    <Drawer
      open
      onClose={panels.active ? panels.back : onClose}
      closeLabel={t(keys.common.close)}
      width={DETAIL_WIDTH}
      crumb={store ? `${listTitle} / ${store.storeName}` : listTitle}
      badge={panels.active ? undefined : store ? (isRestaurant ? "servicebell" : "cart-world") : undefined}
      title={panels.active ? undefined : store?.storeName}
      sub={
        panels.active || !store ? undefined : (
          <>
            {subParts.map((part, index) => (
              <span key={index}>
                {index > 0 ? " · " : null}
                {part}
              </span>
            ))}
          </>
        )
      }
      actions={panels.active ? panels.barSlot : barActions}
    >
      {panels.active ? panels.bodySlot : null}
      {/* Kept mounted (hidden) under an open panel: the panel's owner lives here. */}
      <div style={{ display: panels.active ? "none" : "flex", flexDirection: "column", gap: 16 }}>{body}</div>
    </Drawer>,
  );
}
